import { useState } from 'react'
import { Link } from 'react-router-dom'
import { cn } from '@/shared/ui/primitives'
import { relativeTime } from '@/shared/format'
import { Icon } from '@/app/icons'
import type { Pipeline, RecentDocument, ReturnedDocument } from '../api'
import { countOf, IN_PROGRESS, money, monthLabel, PIPELINE_STAGES } from './display'
import { CARD, CardHead, EmptyRow, StatusPill } from './ui'

/**
 * The drafter's side of the landing page: what came back, where this month's
 * documents are, and what was touched last.
 */

/** Returned documents, each with the reason it came back. */
export function ReturnedCard({ documents, total }: { documents: ReturnedDocument[]; total: number }) {
  return (
    <section className={CARD} aria-label="Dikembalikan ke Anda">
      <CardHead
        title="Dikembalikan ke Anda"
        count={total}
        countTone="danger"
        link={{ to: '/billing/proforma?status=RETURNED', label: 'Lihat semua' }}
      />
      {documents.length === 0 ? (
        <EmptyRow
          title="Tidak ada yang perlu diperbaiki"
          text="Proforma yang dikembalikan penyetuju akan muncul di sini beserta alasannya."
        />
      ) : (
        <ul className="divide-y divide-line">
          {documents.map((doc) => (
            <li key={doc.id}>
              <Link
                to={`/billing/proforma/${doc.id}`}
                className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-4 px-5 py-3.5 text-gray-700 hover:bg-gray-100 hover:text-gray-700"
              >
                <span className="min-w-0">
                  <span className="block font-mono font-semibold text-gray-900">{doc.docNo ?? 'Tanpa nomor'}</span>
                  <span className="block truncate text-gray-500">{doc.customerName ?? '—'}</span>
                  <span className="mt-1.5 block rounded-lg bg-danger-light px-2.5 py-2 text-[0.8125rem] text-gray-800">
                    {doc.note ? (
                      <>
                        {doc.returnedBy && <b className="font-semibold text-danger">{doc.returnedBy}: </b>}
                        {doc.note}
                      </>
                    ) : (
                      'Alasan pengembalian tidak tercatat.'
                    )}
                  </span>
                </span>
                <span className="text-right font-semibold whitespace-nowrap text-gray-900 tabular-nums">
                  {money(doc.currency, doc.totalAmt)}
                  {doc.returnedAt && (
                    <small className="block text-xs font-normal text-gray-500">{relativeTime(doc.returnedAt)}</small>
                  )}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

/** Where this month's documents are, as five stages and the loops back. */
export function PipelineCard({ pipeline }: { pipeline: Pipeline }) {
  const returned = pipeline.byStatus.RETURNED ?? 0
  const failed = pipeline.byStatus.POST_FAILED ?? 0

  return (
    <section className={CARD} aria-label="Alur proforma Anda">
      <CardHead
        title="Alur proforma Anda"
        sub={monthLabel(pipeline.from)}
        link={{ to: '/billing/proforma', label: 'Semua proforma' }}
      />
      <div className="overflow-x-auto px-5 pt-5 pb-4">
        <div className="relative grid min-w-[35rem] grid-cols-5">
          <span
            aria-hidden="true"
            className="absolute top-[21px] right-[10%] left-[10%] h-[3px] rounded-full opacity-45"
            style={{ background: `linear-gradient(90deg, ${PIPELINE_STAGES.map((stage) => stage.color).join(', ')})` }}
          />
          {PIPELINE_STAGES.map((stage) => {
            const count = countOf(pipeline.byStatus, stage.statuses)
            return (
              <Link
                key={stage.label}
                to={`/billing/proforma?status=${stage.statuses[0]}`}
                className="group relative grid justify-items-center gap-2 rounded-xl px-1.5 text-center text-gray-700 hover:text-gray-700"
              >
                <span
                  className={cn(
                    'grid size-11 place-items-center rounded-full border-[3px] bg-card text-base font-extrabold tabular-nums transition group-hover:scale-105',
                    count === 0 ? 'border-dashed text-gray-400' : 'text-gray-900',
                  )}
                  style={{ borderColor: stage.color }}
                >
                  {count}
                </span>
                <b className="text-[0.8rem] leading-tight font-semibold">{stage.label}</b>
                <span className="-mt-1.5 text-xs text-gray-500">{stage.hint}</span>
              </Link>
            )
          })}
        </div>
      </div>
      {(returned > 0 || failed > 0) && (
        <div className="grid gap-2 px-5 pb-5">
          {returned > 0 && (
            <LoopLine to="/billing/proforma?status=RETURNED" icon="undo">
              <b className="text-danger">{returned} dikembalikan</b> penyetuju dan menunggu diperbaiki
            </LoopLine>
          )}
          {failed > 0 && (
            <LoopLine to="/billing/proforma?status=POST_FAILED" icon="flag">
              <b className="text-danger">{failed} gagal dikirim</b> ke ERP dan perlu dicek
            </LoopLine>
          )}
        </div>
      )}
    </section>
  )
}

function LoopLine({ to, icon, children }: { to: string; icon: 'undo' | 'flag'; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2.5 rounded-lg border border-dashed border-danger/45 px-3 py-2.5 text-[0.8125rem] text-gray-700">
      <Icon name={icon} className="size-4 shrink-0 text-danger" />
      <span className="min-w-0 flex-1">{children}</span>
      <Link to={to} className="font-semibold whitespace-nowrap text-primary">
        Lihat
      </Link>
    </div>
  )
}

type Filter = 'all' | 'active' | 'done'

/** The documents this person touched last, with a quick split by whether they are finished. */
export function RecentCard({ documents }: { documents: RecentDocument[] }) {
  const [filter, setFilter] = useState<Filter>('all')
  const shown = documents.filter((doc) =>
    filter === 'all' ? true : filter === 'active' ? IN_PROGRESS.has(doc.status) : !IN_PROGRESS.has(doc.status),
  )

  const chip = (value: Filter, label: string) => (
    <button
      type="button"
      aria-pressed={filter === value}
      onClick={() => setFilter(value)}
      className={cn(
        'rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
        filter === value ? 'border-gray-900 bg-gray-900 text-card' : 'border-line text-gray-500 hover:text-gray-900',
      )}
    >
      {label}
    </button>
  )

  return (
    <section className={CARD} aria-label="Dokumen terakhir Anda">
      <div className="flex flex-wrap items-center gap-2.5 border-b border-line px-5 py-4">
        <h2 className="text-base font-bold tracking-tight text-gray-900">Dokumen terakhir Anda</h2>
        {documents.length > 0 && (
          <div className="ml-auto flex flex-wrap gap-1.5">
            {chip('all', 'Semua')}
            {chip('active', 'Masih berjalan')}
            {chip('done', 'Selesai')}
          </div>
        )}
      </div>
      {documents.length === 0 ? (
        <EmptyRow
          good={false}
          icon={<Icon name="document" className="size-5" />}
          title="Belum ada dokumen"
          text="Proforma yang Anda buat atau ubah akan muncul di sini."
        />
      ) : shown.length === 0 ? (
        <p className="px-5 py-6 text-gray-500">Tidak ada dokumen dengan filter ini.</p>
      ) : (
        <ul className="divide-y divide-line">
          {shown.map((doc) => (
            <li key={doc.id}>
              <Link
                to={`/billing/proforma/${doc.id}`}
                className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-1.5 px-5 py-3 text-gray-700 hover:bg-gray-100 hover:text-gray-700 md:grid-cols-[minmax(0,1fr)_11rem_10rem_6rem]"
              >
                <span className="min-w-0">
                  <span className="block font-mono font-semibold text-gray-900">{doc.docNo ?? 'Tanpa nomor'}</span>
                  <span className="block truncate text-gray-500">{doc.customerName ?? '—'}</span>
                </span>
                <span className="row-start-2 md:row-start-auto">
                  <StatusPill status={doc.status} />
                </span>
                <span className="col-start-2 row-start-1 text-right font-medium whitespace-nowrap text-gray-900 tabular-nums md:col-start-auto md:row-start-auto">
                  {money(doc.currency, doc.totalAmt)}
                </span>
                <span className="col-start-2 row-start-2 text-right text-xs whitespace-nowrap text-gray-500 md:col-start-auto md:row-start-auto">
                  {relativeTime(doc.updatedAt)}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
